import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { AppLanguage } from '../i18n';
import { getRouteNarration, type NarrationTrack } from './narrationContent';
import { normalizeNarration, sortVoices, splitNarration } from './narrationUtils';

type NarrationStatus = 'idle' | 'playing' | 'paused' | 'error';

type NarrationContextValue = {
  supported: boolean;
  status: NarrationStatus;
  tracks: NarrationTrack[];
  activeTrack: NarrationTrack | null;
  activeIndex: number;
  rate: number;
  voices: SpeechSynthesisVoice[];
  selectedVoiceURI: string;
  selectedVoice: SpeechSynthesisVoice | null;
  playPage: () => void;
  playTrack: (track: NarrationTrack) => void;
  playTrackById: (id: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  previous: () => void;
  next: () => void;
  setRate: (rate: number) => void;
  setVoice: (voiceURI: string) => void;
  previewVoice: (voiceURI: string) => void;
  setPageTracks: (tracks: NarrationTrack[] | null) => void;
};

const NarrationContext = createContext<NarrationContextValue | null>(null);

function getLanguage(value?: string): AppLanguage {
  return value?.startsWith('en') ? 'en' : 'id';
}

function voiceStorageKey(language: AppLanguage) {
  return `amertasala-narration-voice-${language}`;
}

function rateStorageKey(language: AppLanguage) {
  return `amertasala-narration-rate-${language}`;
}

export function NarrationProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const language = getLanguage(i18n.resolvedLanguage);
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [customTracks, setCustomTracks] = useState<NarrationTrack[] | null>(null);
  const [status, setStatus] = useState<NarrationStatus>('idle');
  const [activeTrack, setActiveTrack] = useState<NarrationTrack | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState('');
  const [rate, setRateState] = useState(language === 'id' ? 0.92 : 0.96);
  const sessionRef = useRef(0);
  const playAllRef = useRef(false);
  const tracksRef = useRef<NarrationTrack[]>([]);

  const routeTracks = useMemo(() => getRouteNarration(location.pathname, t), [location.pathname, t, i18n.resolvedLanguage]);
  const tracks = customTracks ?? routeTracks;
  tracksRef.current = tracks;

  const languageVoices = useMemo(() => sortVoices(voices, language), [voices, language]);
  const selectedVoice = useMemo(
    () => languageVoices.find((voice) => voice.voiceURI === selectedVoiceURI) ?? languageVoices[0] ?? null,
    [languageVoices, selectedVoiceURI],
  );

  useEffect(() => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    const refresh = () => setVoices(synth.getVoices());
    refresh();
    synth.addEventListener?.('voiceschanged', refresh);
    const timer = window.setTimeout(refresh, 250);
    return () => {
      window.clearTimeout(timer);
      synth.removeEventListener?.('voiceschanged', refresh);
    };
  }, [supported]);

  useEffect(() => {
    const storedVoice = window.localStorage.getItem(voiceStorageKey(language)) ?? '';
    const storedRate = Number(window.localStorage.getItem(rateStorageKey(language)));
    setSelectedVoiceURI(storedVoice);
    setRateState(Number.isFinite(storedRate) && storedRate >= 0.75 && storedRate <= 1.25 ? storedRate : language === 'id' ? 0.92 : 0.96);
  }, [language]);

  useEffect(() => {
    if (!selectedVoiceURI && languageVoices[0]) setSelectedVoiceURI(languageVoices[0].voiceURI);
  }, [languageVoices, selectedVoiceURI]);

  const stop = useCallback(() => {
    sessionRef.current += 1;
    playAllRef.current = false;
    if (supported) window.speechSynthesis.cancel();
    setStatus('idle');
    setActiveTrack(null);
    setActiveIndex(-1);
  }, [supported]);

  useEffect(() => {
    setCustomTracks(null);
    stop();
  }, [location.pathname, language, stop]);

  const speakSequence = useCallback(async (track: NarrationTrack, index: number, continuePlaylist: boolean) => {
    if (!supported || !track.text.trim()) return;
    const synth = window.speechSynthesis;
    const currentSession = ++sessionRef.current;
    playAllRef.current = continuePlaylist;
    synth.cancel();
    setActiveTrack(track);
    setActiveIndex(index);
    setStatus('playing');

    const chunks = splitNarration(normalizeNarration(track.text, language));
    try {
      for (const chunk of chunks) {
        if (sessionRef.current !== currentSession) return;
        await new Promise<void>((resolve) => {
          const utterance = new SpeechSynthesisUtterance(chunk);
          utterance.lang = language === 'id' ? 'id-ID' : 'en-US';
          utterance.voice = selectedVoice;
          utterance.rate = rate;
          utterance.pitch = 1;
          utterance.volume = 1;
          utterance.onend = () => resolve();
          utterance.onerror = () => resolve();
          synth.speak(utterance);
        });
        if (sessionRef.current !== currentSession) return;
        await new Promise((resolve) => window.setTimeout(resolve, 240));
      }

      if (sessionRef.current !== currentSession) return;
      const list = tracksRef.current;
      if (continuePlaylist && index >= 0 && index + 1 < list.length) {
        await speakSequence(list[index + 1], index + 1, true);
      } else {
        setStatus('idle');
        setActiveTrack(null);
        setActiveIndex(-1);
        playAllRef.current = false;
      }
    } catch {
      if (sessionRef.current === currentSession) setStatus('error');
    }
  }, [language, rate, selectedVoice, supported]);

  const playPage = useCallback(() => {
    if (!tracksRef.current.length) return;
    void speakSequence(tracksRef.current[0], 0, true);
  }, [speakSequence]);

  const playTrack = useCallback((track: NarrationTrack) => {
    const index = tracksRef.current.findIndex((item) => item.id === track.id);
    void speakSequence(track, index, false);
  }, [speakSequence]);

  const playTrackById = useCallback((id: string) => {
    const index = tracksRef.current.findIndex((item) => item.id === id);
    if (index < 0) return;
    void speakSequence(tracksRef.current[index], index, false);
  }, [speakSequence]);

  const pause = useCallback(() => {
    if (!supported || status !== 'playing') return;
    window.speechSynthesis.pause();
    setStatus('paused');
  }, [status, supported]);

  const resume = useCallback(() => {
    if (!supported || status !== 'paused') return;
    window.speechSynthesis.resume();
    setStatus('playing');
  }, [status, supported]);

  const previous = useCallback(() => {
    const list = tracksRef.current;
    const index = activeIndex > 0 ? activeIndex - 1 : 0;
    if (list[index]) void speakSequence(list[index], index, playAllRef.current);
  }, [activeIndex, speakSequence]);

  const next = useCallback(() => {
    const list = tracksRef.current;
    const index = activeIndex >= 0 && activeIndex + 1 < list.length ? activeIndex + 1 : 0;
    if (list[index]) void speakSequence(list[index], index, playAllRef.current);
  }, [activeIndex, speakSequence]);

  const setRate = useCallback((nextRate: number) => {
    const safeRate = Math.min(1.25, Math.max(0.75, nextRate));
    setRateState(safeRate);
    window.localStorage.setItem(rateStorageKey(language), String(safeRate));
  }, [language]);

  const setVoice = useCallback((voiceURI: string) => {
    setSelectedVoiceURI(voiceURI);
    window.localStorage.setItem(voiceStorageKey(language), voiceURI);
    stop();
  }, [language, stop]);

  const previewVoice = useCallback((voiceURI: string) => {
    if (!supported) return;
    sessionRef.current += 1;
    window.speechSynthesis.cancel();
    const voice = voices.find((item) => item.voiceURI === voiceURI) ?? selectedVoice;
    const utterance = new SpeechSynthesisUtterance(t('narration.previewText'));
    utterance.lang = language === 'id' ? 'id-ID' : 'en-US';
    utterance.voice = voice;
    utterance.rate = rate;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  }, [language, rate, selectedVoice, supported, t, voices]);

  const value = useMemo<NarrationContextValue>(() => ({
    supported,
    status,
    tracks,
    activeTrack,
    activeIndex,
    rate,
    voices: languageVoices,
    selectedVoiceURI: selectedVoice?.voiceURI ?? '',
    selectedVoice,
    playPage,
    playTrack,
    playTrackById,
    pause,
    resume,
    stop,
    previous,
    next,
    setRate,
    setVoice,
    previewVoice,
    setPageTracks: setCustomTracks,
  }), [supported, status, tracks, activeTrack, activeIndex, rate, languageVoices, selectedVoice, playPage, playTrack, playTrackById, pause, resume, stop, previous, next, setRate, setVoice, previewVoice]);

  return <NarrationContext.Provider value={value}>{children}</NarrationContext.Provider>;
}

export function useNarration() {
  const context = useContext(NarrationContext);
  if (!context) throw new Error('useNarration must be used inside NarrationProvider');
  return context;
}
